import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { Divider, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoom, DataRoomTeaserFile, UpdateDataRoomDto } from '../../shared/models';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface TeaserSummaryFormProps {
	dataRoomGuid: string | undefined;
	isOpen: boolean;
	dataRoom: DataRoom | undefined | null;
	close: () => void;
	onDeleteFile: (file: DataRoomTeaserFile) => void;
}

export const TeaserSummaryForm: React.FC<TeaserSummaryFormProps> = ({
	dataRoomGuid,
	isOpen,
	dataRoom,
	close,
	onDeleteFile,
}) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const ref = React.useRef<HTMLDivElement>(null);

	const summaryMutation = useMutation(
		async ({ values }: TeaserSummaryFormMutationVariables) => {
			let filesUpdate: DataRoomTeaserFile | null = null;

			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<DataRoomTeaserFile>(
					api.post(CerebroApiEndpoints.Teasers.Upload(dataRoomGuid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}
				filesUpdate = data;
			}

			const dataRoomPatch: UpdateDataRoomDto = {
				roomSummaryText: values.summaryText,
				page: 'Summary',
			};

			const { data, error } = await getApiResponseData<DataRoom>(
				api.patch(CerebroApiEndpoints.DataRooms.Root(dataRoomGuid), dataRoomPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return { filesUpdate, data };
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoom.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Teasers.Preview(dataRoomGuid));
				snackbar.enqueueSnackbar('Data room was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={isOpen} onClose={() => close()}>
			<Formik
				initialValues={{
					summaryText: dataRoom?.roomSummaryText,
					uploads: [] as File[],
				}}
				onSubmit={FormikUtils.MutateOnSubmit(summaryMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="summaryText">Data Room Summary/Teaser Email Text</CerebroLabel>
									<CerebroRichText
										placeholder="Enter Summary Text Here..."
										value={values.summaryText ?? ''}
										onChange={updatedValue => setFieldValue('summaryText', updatedValue)}
									/>
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="uploads">Attachments</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={false}
										multiple={true}
										sx={{ marginY: 3, width: '100%' }}
									/>
								</Grid>

								{dataRoom?.teaserFiles && dataRoom.teaserFiles.length > 0 && (
									<Grid item xs={11}>
										<CerebroText variant="small" weight="medium" sx={{ marginTop: 3 }}>
											Attached Files
										</CerebroText>
										<Divider sx={{ marginY: 1 }} />
										<List dense disablePadding>
											{dataRoom.teaserFiles.map(file => {
												return (
													<ListItem
														dense
														key={file.guid}
														disableGutters
														secondaryAction={
															<IconButton
																onClick={() => onDeleteFile(file)}
																sx={theme => ({ color: theme.palette.error.main })}
															>
																<FontAwesomeIcon size="sm" icon={faTrash} />
															</IconButton>
														}
													>
														<ListItemButton
															component="a"
															target="_blank"
															rel="noreferrer"
															href={CerebroApiEndpoints.Teasers.AbsoluteDownload(
																dataRoomGuid,
																file.guid
															)}
														>
															<ListItemIcon
																sx={theme => ({
																	color: theme.palette.primary.main,
																	minWidth: 30,
																})}
															>
																<FontAwesomeIcon icon={faPaperclip} />
															</ListItemIcon>
															<ListItemText>{file.title}</ListItemText>
														</ListItemButton>
													</ListItem>
												);
											})}
										</List>
									</Grid>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type TeaserSummaryFormMutationVariables = FormikUtils.MutateOnSubmitVariables<TeaserSummaryFormValues>;
interface TeaserSummaryFormValues {
	summaryText: string | undefined;
	uploads: File[];
}
type TeaserResponseFormValueErrors = FormikErrors<TeaserSummaryFormValues>;
